import Axios from 'api/client';
import {
    CustomFieldRequestValue,
    IndexedTransaction,
    PledgeTransaction,
    SoftCredit,
    SoftCreditParams,
    Transaction,
    TransactionDonation,
    TransactionLineItem,
} from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface HasAccountIdParam {
    accountId: number;
}
interface HasTransactionIdParam {
    transactionId: number;
}
interface HasItemIdParam {
    itemId: number;
}

type TransactionItemTypeParameter = 'donation' | 'tip' | 'item' | 'product';
interface FetchTransactionItemsParams extends FilterParams, HasAccountIdParam {
    transaction_id?: number;
    campaigns?: number[];
    type?: TransactionItemTypeParameter;
    fulfilled?: boolean;
    auctions?: number[];
}

interface FetchTransactionsParams extends FilterParams {
    plan_id?: number;
    without?: string;
}

interface FulfillTransactionItemParams
    extends HasAccountIdParam,
        HasTransactionIdParam,
        HasItemIdParam {}

interface UpdateTransactionParams extends Partial<Transaction> {
    offline_payment_received?: boolean | null;
}
interface UpdateTransactionCustomField {
    [field: string]: CustomFieldRequestValue;
}

interface UpdateTransactionCustomFieldParams {
    custom_fields: UpdateTransactionCustomField;
}

const fetchTransactions = (accountId: number, params: FetchTransactionsParams = {}) => {
    const { scope = 'all', eager = false, filters, include, ...restParams } = params;

    return Axios.post<PaginatedData<IndexedTransaction>>(
        `/accounts/${accountId}/get_transactions`,
        {
            ...filters,
            ...restParams,
            scope,
            include:
                (include ?? eager)
                    ? 'fund,team,member,dedication,paymentMethod,externalIds,discountCode,softCredits.contact,giving_space'
                    : '',
        },
    ).then(response => response.data);
};

const fetchTransactionById = (transactionId: number) =>
    Axios.get<Transaction>(`/transactions/${transactionId}`).then(response => response.data);

const updateTransaction = (transactionId: number, data: UpdateTransactionParams) =>
    Axios.patch<Transaction>(`/transactions/${transactionId}`, data).then(
        response => response.data,
    );

const fetchTransactionItems = ({ accountId, ...params }: FetchTransactionItemsParams) =>
    Axios.get<PaginatedData<TransactionLineItem>>(`/accounts/${accountId}/transaction_items`, {
        params,
    }).then(response => response.data);

const updateTransactionCustomFields = (
    transactionId: number,
    customFields: UpdateTransactionCustomFieldParams,
) => Axios.patch(`/transactions/${transactionId}`, customFields).then(response => response.data);

const fulfillTransactionItem = ({
    accountId,
    transactionId,
    itemId,
}: FulfillTransactionItemParams) =>
    Axios.post<TransactionLineItem>(
        `/accounts/${accountId}/transactions/${transactionId}/items/${itemId}/fulfill`,
    ).then(response => response.data);

const unfulfillTransactionItem = ({
    accountId,
    transactionId,
    itemId,
}: FulfillTransactionItemParams) =>
    Axios.post<TransactionLineItem>(
        `/accounts/${accountId}/transactions/${transactionId}/items/${itemId}/unfulfill`,
    ).then(response => response.data);

const toggleFulfillTransactionItem = (shouldFulfill: boolean) =>
    shouldFulfill ? fulfillTransactionItem : unfulfillTransactionItem;

const toggleAcknowledgeTransaction = (transactionId: number, shouldAcknowledge: boolean) =>
    Axios.post<Transaction>(
        `/transactions/${transactionId}/${shouldAcknowledge ? 'acknowledge' : 'unacknowledge'}`,
    ).then(response => response.data);

const refundTransaction = (accountId: number, transactionId: number, params: { reason: string }) =>
    Axios.post(`/accounts/${accountId}/transactions/${transactionId}/refund`, params).then(
        response => response.data,
    );

const donate = (params: TransactionDonation | PledgeTransaction) =>
    Axios.post<TransactionLineItem>(
        `/donate`,
        {
            is_external: true,
            ...params,
        },
        { params: { step: 'dashboard' } },
    ).then(response => response.data);

const sendTransactionReceipt = (transactionId: number, params: { email: string }) =>
    Axios.post<Transaction>(`/transactions/${transactionId}/send_receipt`, params).then(
        response => response.data,
    );

const deleteTransactionById = (transactionId: number) =>
    Axios.delete<Transaction>(`/transactions/${transactionId}`).then(response => response.data);

const deleteTransactionDedication = (transactionId: number) =>
    Axios.delete<Transaction>(`/transactions/${transactionId}/dedication`).then(
        response => response.data,
    );

const reassignTransaction = (transactionId: number, params: { campaign_id: number }) =>
    Axios.post(`/transactions/${transactionId}/reassign_campaign`, params).then(
        response => response.data,
    );

const createSoftCredit = (accountId: number, transactionId: number, params: SoftCreditParams) =>
    Axios.post<SoftCredit>(
        `/accounts/${accountId}/transactions/${transactionId}/soft-credits`,
        params,
    ).then(response => response.data);

const fetchSoftCreditById = (accountId: number, transactionId: number, softCreditId: number) =>
    Axios.get<SoftCredit>(
        `/accounts/${accountId}/transactions/${transactionId}/soft-credits/${softCreditId}`,
    ).then(response => response.data);

const updateSoftCreditById = (
    accountId: number,
    transactionId: number,
    softCreditId: number,
    params: Partial<SoftCreditParams>,
) =>
    Axios.patch<SoftCredit>(
        `/accounts/${accountId}/transactions/${transactionId}/soft-credits/${softCreditId}`,
        params,
    ).then(response => response.data);

const deleteSoftCreditById = (accountId: number, transactionId: number, softCreditId: number) =>
    Axios.delete(
        `/accounts/${accountId}/transactions/${transactionId}/soft-credits/${softCreditId}`,
    ).then(response => response.data);

export {
    updateTransaction,
    fetchTransactions,
    fetchTransactionById,
    fetchTransactionItems,
    fulfillTransactionItem,
    unfulfillTransactionItem,
    toggleFulfillTransactionItem,
    toggleAcknowledgeTransaction,
    refundTransaction,
    donate,
    sendTransactionReceipt,
    deleteTransactionById,
    deleteTransactionDedication,
    updateTransactionCustomFields,
    reassignTransaction,
    createSoftCredit,
    fetchSoftCreditById,
    updateSoftCreditById,
    deleteSoftCreditById,
};
export type { FetchTransactionItemsParams, FetchTransactionsParams };
